<div class="page-container defects-page">
  <div class="header-section">
    <div class="title-block">
      <h1>Defects</h1>
      <div class="subtitle">{{ catalogSummary }}</div>
    </div>

    <div class="header-actions">
      <button class="btn-primary" (click)="toggleCreateDialog()" [disabled]="!hasCustomerSelected">
        <span class="material-icons">add</span>
        New Defect
      </button>
    </div>
  </div>

  <div class="filter-bar">
    <div class="filter-bar-grid">
      <div class="form-group customer-group">
        <label class="form-label">Customer</label>
        <p-select [filter]="true" [options]="customers" optionLabel="title"
          [(ngModel)]="selectedCustomer" (ngModelChange)="onCustomerChange()"
         >
        </p-select>
      </div>

      <div class="search-box defects-search">
        <span class="material-icons search-icon">search</span>
        <input pInputText [ngModel]="searchText" (ngModelChange)="onSearchInput($event)"
          [disabled]="!hasCustomerSelected" placeholder='Search defects, for example "stain" or "1.1.1"'
          class="search-input" aria-label="Search defects" />
        <button *ngIf="searchText" type="button" class="clear-search-btn" (click)="clearSearch()"
          aria-label="Clear search">
          <span class="material-icons">close</span>
        </button>
      </div>
    </div>

    <div class="filter-chips" role="toolbar" aria-label="Severity filters">
      <button type="button" class="filter-chip" [class.is-active]="severityFilter === 'all'"
        (click)="setSeverityFilter('all')">
        All
      </button>
      <button type="button" class="filter-chip is-critical" [class.is-active]="severityFilter === 'critical'"
        (click)="setSeverityFilter('critical')">
        <span class="material-icons">priority_high</span>
        Critical
      </button>
      <button type="button" class="filter-chip is-major" [class.is-active]="severityFilter === 'major'"
        (click)="setSeverityFilter('major')">
        <span class="material-icons">warning</span>
        Major
      </button>
      <button type="button" class="filter-chip is-minor" [class.is-active]="severityFilter === 'minor'"
        (click)="setSeverityFilter('minor')">
        <span class="material-icons">report</span>
        Minor
      </button>
    </div>
  </div>

  <nav *ngIf="hasCustomerSelected" class="breadcrumb-bar" aria-label="Defect path">
    <span class="material-icons breadcrumb-pin">place</span>

    <ng-container *ngFor="let segment of focusedBreadcrumb; trackBy: trackByBreadcrumb; let i = index; let last = last">
      <button type="button" class="breadcrumb-segment" [class.is-last]="last" (click)="goToBreadcrumb(i)">
        {{ segment.title }}
      </button>
      <span *ngIf="!last" class="breadcrumb-sep" aria-hidden="true">›</span>
    </ng-container>

    <button type="button" class="breadcrumb-clear" (click)="setFocus(null)" title="Back to root">
      <span class="material-icons">close</span>
    </button>
  </nav>

  <div *ngIf="loading" class="loading-container">
    <p-progressSpinner styleClass="labtech-spinner" ariaLabel="Loading"></p-progressSpinner>
    <p>Loading defects...</p>
  </div>

  <div *ngIf="!loading && !hasCustomerSelected" class="empty-state">
    <span class="material-icons empty-icon">person_search</span>
    <h3>No customer selected</h3>
    <p>Please select a customer to load the defect catalog.</p>
  </div>

  <div *ngIf="!loading && hasCustomerSelected" class="defects-layout">
    <section class="tree-pane" aria-label="Defect catalog tree">
      <ng-container *ngIf="searchText; else defectTree">
        <h2 class="pane-title">
          <span class="material-icons">search</span>
          Results
          <span class="pane-counter">{{ searchResults.length }}</span>
        </h2>

        <div *ngIf="searchResults.length === 0" class="empty-state empty-state--compact">
          <span class="material-icons empty-icon">search_off</span>
          <h3>No results</h3>
          <p>Try a different spelling or reset the filter.</p>
        </div>

        <ul *ngIf="searchResults.length > 0" class="result-list">
          <li *ngFor="let result of searchResults; trackBy: trackByResult" class="result-item">
            <div class="result-main">
              <span class="result-code">{{ result.defect.number }}</span>

              <div class="result-text">
                <div class="result-title-row">
                  <span class="result-name">{{ displayTitle(result.defect) }}</span>
                  <span *ngIf="secondaryTitle(result.defect)" class="result-alt">{{ secondaryTitle(result.defect)
                    }}</span>
                </div>

                <div class="result-path">
                  <ng-container *ngFor="let segment of result.pathSegments; let last = last">
                    <span [class.is-last]="last">{{ segment.title }}</span>
                    <span *ngIf="!last" class="result-path-sep" aria-hidden="true">›</span>
                  </ng-container>
                </div>
              </div>
            </div>

            <div class="result-actions">
              <button type="button" class="btn-secondary btn-locate" (click)="selectFromSearch(result)">
                <span class="material-icons">my_location</span>
                Show in tree
              </button>
            </div>
          </li>
        </ul>
      </ng-container>

      <ng-template #defectTree>
        <h2 class="pane-title">
          <span class="material-icons">account_tree</span>
          Catalog
          <span class="pane-hint">Breadcrumb + tree like execute inspection ✨</span>
        </h2>

        <div *ngIf="tree.length === 0" class="empty-state empty-state--compact">
          <span class="material-icons empty-icon">rule_folder</span>
          <h3>No defect catalog available</h3>
          <p>Create the first defect for this customer.</p>
        </div>

        <div *ngIf="tree.length > 0" class="tree-root" role="tree" aria-label="Defect tree">
          <app-defect-admin-tree-node *ngFor="let root of tree; trackBy: trackByTreeNode" [node]="root"
            [expandedIds]="expandedIds" [focusedId]="focusedId" (nodeClick)="onTreeNodeClick($event)"
            (focusChange)="setFocus($event)">
          </app-defect-admin-tree-node>
        </div>
      </ng-template>
    </section>

    <aside class="detail-pane" aria-label="Defect editor">
      <div class="detail-summary">
        <h2 class="pane-title">
          <span class="material-icons">edit_note</span>
          {{ detailPaneTitle }}
        </h2>
        <button *ngIf="focusedDefectIsDeletable && focusedDefect" type="button" class="defect-header-delete"
          title="Delete defect" (click)="openDeleteDialog(focusedDefect!)">
          <span class="material-icons">delete</span>
          Delete
        </button>
        <span class="pane-counter">{{ detailCards.length }}</span>
      </div>
      <div class="detail-subtitle">{{ detailPaneSubtitle }}</div>
      <div *ngIf="detailCards.length === 0" class="empty-state empty-state--compact">
        <span class="material-icons empty-icon">inventory_2</span>
        <h3>No matching defects</h3>
        <p>Adjust the severity filter or create a new defect in this branch.</p>
      </div>
      <div *ngIf="detailCards.length > 0" class="detail-card-list">
        <app-defect-card *ngFor="let defect of detailCards; trackBy: trackByCard" [defect]="defect"
          [customerId]="selectedCustomerId!" (openChildren)="openChildren($event)" (changed)="reloadCatalog()">
        </app-defect-card>
      </div>
    </aside>
  </div>

  <ng-container *ngIf="createDialog">
    <p-dialog [modal]="true" [visible]="true" header="New Defect" (onHide)="toggleCreateDialog()" styleClass="defect-create-dialog" [style]="{ width: '680px' }">
      <div class="form-card defect-create-card">
        <div class="dialog-context">
          <span class="material-icons">place</span>
          <div>
            <div class="dialog-context-label">Create under</div>
            <strong>{{ createContextLabel }}</strong>
          </div>
        </div>

        <div class="form-grid-layout">
          <div class="form-group full-width">
            <label class="form-label">Title (DE)</label>
            <input pInputText [(ngModel)]="newDefect.titleDE" />
          </div>

          <div class="form-group full-width">
            <label class="form-label">Title (EN)</label>
            <input pInputText [(ngModel)]="newDefect.titleEN" />
          </div>

          <div class="form-group">
            <label class="form-label">Number</label>
            <p-inputNumber [(ngModel)]="newDefect.number" [minFractionDigits]="0" [maxFractionDigits]="0">
            </p-inputNumber>
          </div>

          <div class="form-group full-width defect-create-severity">
            <label class="form-label">Severity</label>
            <div class="toggle-grid">
              <label class="toggle-item">
                <input type="checkbox" [(ngModel)]="newDefect.critical" />
                Critical
              </label>

              <label class="toggle-item">
                <input type="checkbox" [(ngModel)]="newDefect.major" />
                Major
              </label>

              <label class="toggle-item">
                <input type="checkbox" [(ngModel)]="newDefect.minor" />
                Minor
              </label>
            </div>

            <div class="hint">
              Severity is only relevant for leaf defects. As soon as a defect gets children, severity is cleared
              automatically.
            </div>
          </div>
        </div>
      </div>

      <ng-template pTemplate="footer">
        <button class="btn-secondary" (click)="toggleCreateDialog()">Cancel</button>
        <button class="btn-primary" (click)="createDefect()">Create</button>
      </ng-template>
    </p-dialog>
  </ng-container>

  <!-- ✅ NEU: Delete Dialog -->
  <ng-container *ngIf="deleteDialog && defectToDelete">
    <p-dialog [modal]="true" [visible]="true" header="Delete Defect" (onHide)="closeDeleteDialog()" [style]="{ width: '450px' }">
      <div class="dialog-content">
        <p>Are you sure you want to delete defect
          <strong>{{ displayTitle(defectToDelete) }}</strong>?
        </p>
        <p class="dialog-warning-text">This action cannot be undone.</p>
      </div>
      <ng-template pTemplate="footer">
        <button class="btn-secondary" (click)="closeDeleteDialog()">Cancel</button>
        <button class="btn-primary" (click)="confirmDeleteDefect()">
          <span class="material-icons">delete</span>
          Delete
        </button>
      </ng-template>
    </p-dialog>
  </ng-container>
</div>
